import { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { PoundSterling, ArrowLeft, Search, X, Loader2, ShoppingCart, Plus, Minus, Printer, Download, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import wagoLogo from '../../../assets/wago-logo.png';
import { toolsAPI } from '../../../api';
import { useToast } from '../../../hooks/useToast';
import SlideOver from '../../../components/ui/SlideOver';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import api from '../../../api';

export default function WagoPriceList() {
  const navigate = useNavigate();
  const { error: showError, success, ToastContainer } = useToast();
  
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Cart state
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Search state
  const [search, setSearch] = useState('');
  const [articleNo, setArticleNo] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  
  // Debounce timer
  const [searchTimer, setSearchTimer] = useState(null);

  useEffect(() => {
    fetchPriceList();
  }, [page, search, articleNo, description, category]);

  const fetchPriceList = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        ...(search && !articleNo && !description && !category && { search }),
        ...(articleNo && { article_no: articleNo }),
        ...(description && { description }),
        ...(category && { category }),
      };
      
      const response = await toolsAPI.getWagoPriceList(params);
      
      setData(response.data || []);
      setCategories(response.categories || []);
      setTotal(response.pagination?.total || 0);
      setTotalPages(response.pagination?.totalPages || 0);
    } catch (error) {
      console.error('Error fetching Wago price list:', error);
      showError('Error', error.message || 'Failed to load price list');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    // Clear specific filters when general search is used
    if (value) {
      setArticleNo('');
      setDescription('');
      setCategory('');
    }
    // Reset to first page on search
    setPage(1);
  };

  const handleArticleNoChange = (value) => {
    setArticleNo(value);
    setSearch(''); // Clear general search when specific filter is used
    setPage(1);
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
    setSearch(''); // Clear general search when specific filter is used
    setPage(1);
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    setSearch(''); // Clear general search when specific filter is used
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setArticleNo('');
    setDescription('');
    setCategory('');
    setPage(1);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return `£${parseFloat(value).toFixed(2)}`;
  };

  // Cart functions
  const addToCart = (item) => {
    // Check if item already exists in cart
    const existingIndex = cart.findIndex(cartItem => cartItem.article_no === item.article_no);
    
    if (existingIndex >= 0) {
      // Increment quantity
      const updatedCart = [...cart];
      updatedCart[existingIndex] = {
        ...updatedCart[existingIndex],
        quantity: (updatedCart[existingIndex].quantity || 1) + 1
      };
      setCart(updatedCart);
      success('Item updated', `Increased quantity for ${item.article_no}`);
    } else {
      // Add new item with quantity 1
      setCart([...cart, { ...item, quantity: 1 }]);
      success('Item added', `${item.article_no || 'Item'} added to list`);
    }
  };

  const removeFromCart = (articleNo) => {
    setCart(cart.filter(item => item.article_no !== articleNo));
    success('Item removed', 'Item removed from list');
  };

  const updateCartQuantity = (articleNo, quantity) => {
    if (quantity <= 0) {
      removeFromCart(articleNo);
      return;
    }
    
    const updatedCart = cart.map(item =>
      item.article_no === articleNo
        ? { ...item, quantity: parseInt(quantity) || 1 }
        : item
    );
    setCart(updatedCart);
  };

  const clearCart = () => {
    setCart([]);
    success('List cleared', 'All items removed from list');
  };

  const handlePrint = () => {
    if (cart.length === 0) {
      showError('Empty list', 'Please add items to the list before printing');
      return;
    }

    // Create print-friendly HTML
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintHTML(cart);
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for images to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  const handleDownloadPDF = async () => {
    if (cart.length === 0) {
      showError('Empty list', 'Please add items to the list before downloading');
      return;
    }

    try {
      setGeneratingPdf(true);
      
      const response = await api.post('/tools/wago-price-list/pdf', { items: cart }, {
        responseType: 'blob',
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `wago-price-list-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      success('PDF downloaded', 'Price list PDF has been downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Error', error.message || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const generatePrintHTML = (items) => {
    const date = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    const totalValue = items.reduce((sum, item) => {
      const quantity = item.quantity || 1;
      const price = parseFloat(item.price_each) || 0;
      return sum + (quantity * price);
    }, 0);

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Wago Price List</title>
  <style>
    @media print {
      @page { margin: 15mm; }
      body { margin: 0; }
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 20px;
      color: #1f2937;
    }
    .header {
      text-align: center;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 2px solid #e5e7eb;
    }
    .header h1 {
      margin: 0 0 10px 0;
      font-size: 24px;
      color: #111827;
    }
    .header p {
      margin: 5px 0;
      color: #6b7280;
      font-size: 14px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
    }
    th {
      background-color: #f9fafb;
      padding: 12px;
      text-align: left;
      border-bottom: 2px solid #e5e7eb;
      font-weight: 600;
      font-size: 12px;
      text-transform: uppercase;
      color: #374151;
    }
    td {
      padding: 10px 12px;
      border-bottom: 1px solid #e5e7eb;
      font-size: 14px;
    }
    tr:last-child td {
      border-bottom: 2px solid #e5e7eb;
    }
    .text-right {
      text-align: right;
    }
    .text-center {
      text-align: center;
    }
    .footer {
      margin-top: 30px;
      padding-top: 20px;
      border-top: 2px solid #e5e7eb;
      text-align: right;
    }
    .total {
      font-size: 18px;
      font-weight: 600;
      color: #111827;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Wago Price List</h1>
    <p>Generated: ${date}</p>
    <p>Items: ${items.length}</p>
  </div>
  
  <table>
    <thead>
      <tr>
        <th>Part Number</th>
        <th>Description</th>
        <th>Category</th>
        <th class="text-center">Qty</th>
        <th class="text-right">Pack Qty</th>
        <th class="text-right">Price Each</th>
        <th class="text-right">Price per 100</th>
        <th class="text-right">Line Total</th>
      </tr>
    </thead>
    <tbody>
      ${items.map(item => {
        const quantity = item.quantity || 1;
        const priceEach = parseFloat(item.price_each) || 0;
        const lineTotal = quantity * priceEach;
        
        return `
          <tr>
            <td>${item.article_no || '-'}</td>
            <td>${item.description || '-'}</td>
            <td>${item.category || '-'}</td>
            <td class="text-center">${quantity}</td>
            <td class="text-right">${item.pack_qty || '-'}</td>
            <td class="text-right">£${priceEach.toFixed(2)}</td>
            <td class="text-right">${item.price_per_100 ? '£' + parseFloat(item.price_per_100).toFixed(2) : '-'}</td>
            <td class="text-right">£${lineTotal.toFixed(2)}</td>
          </tr>
        `;
      }).join('')}
    </tbody>
  </table>
  
  <div class="footer">
    <div class="total">Total: ${formatCurrency(totalValue)}</div>
  </div>
</body>
</html>
    `;
  };

  const hasActiveFilters = search || articleNo || description || category;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/tenant/tools/pricing')}
              className="h-8 w-8 p-0 text-xs"
              aria-label="Back to Pricing"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Wago Current Price List</h1>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                View and search Wago product pricing
              </p>
            </div>
          </div>
          
          {/* Cart Button */}
          <Button
            variant="primary"
            size="sm"
            onClick={() => setCartOpen(true)}
            className="relative h-8 gap-2 px-3 text-xs"
          >
            <ShoppingCart className="h-5 w-5" />
            <span>My List</span>
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {cart.length}
              </span>
            )}
          </Button>
        </div>

        {/* Wago Logo Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-white dark:bg-gray-700 rounded-lg flex items-center justify-center p-2">
              <img 
                src={wagoLogo} 
                alt="Wago Logo" 
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Wago Products</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400">Current pricing information</p>
            </div>
          </div>

          {/* Search Section */}
          <div className="space-y-4 mb-6">
            {/* General Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
              <Input
                type="text"
                placeholder="Search by part number, description, or category..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="h-8 pl-10 pr-10"
              />
              {search && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => handleSearch('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Specific Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Article Number Filter */}
              <div>
                <FormLabel>Part Number</FormLabel>
                <Input
                  type="text"
                  placeholder="Filter by part number..."
                  value={articleNo}
                  onChange={(e) => handleArticleNoChange(e.target.value)}
                  className="h-8"
                />
              </div>

              {/* Description Filter */}
              <div>
                <FormLabel>Description</FormLabel>
                <Input
                  type="text"
                  placeholder="Filter by description..."
                  value={description}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  className="h-8"
                />
              </div>

              {/* Category Filter */}
              <div>
                <FormLabel>Category</FormLabel>
                <Select
                  value={category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="h-8"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="h-8 text-xs text-blue-600 dark:text-blue-400"
              >
                Clear all filters
              </Button>
            )}
          </div>

          {/* Results Count */}
          <div className="mb-4 text-xs text-gray-600 dark:text-gray-400">
            Showing {data.length} of {total} products
            {hasActiveFilters && ' (filtered)'}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">Loading price list...</span>
            </div>
          )}

          {/* Price List Table */}
          {!loading && (
            <>
              {data.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-12">
                  <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
                    <PoundSterling className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    No Products Found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md">
                    {hasActiveFilters
                      ? 'Try adjusting your search or filters to find products.'
                      : 'No products in the price list yet.'}
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow isHeader>
                        <TableHead align="left">Part Number</TableHead>
                        <TableHead align="left">Description</TableHead>
                        <TableHead align="left">Category</TableHead>
                        <TableHead align="right">Pack Qty</TableHead>
                        <TableHead align="right">Price Each</TableHead>
                        <TableHead align="right">Price per 100</TableHead>
                        <TableHead align="center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell align="left" className="whitespace-nowrap font-medium">
                            {item.article_no || '-'}
                          </TableCell>
                          <TableCell align="left">
                            {item.description || '-'}
                          </TableCell>
                          <TableCell align="left" className="whitespace-nowrap">
                            {item.category || '-'}
                          </TableCell>
                          <TableCell align="right" className="whitespace-nowrap">
                            {item.pack_qty || '-'}
                          </TableCell>
                          <TableCell align="right" className="whitespace-nowrap font-medium">
                            {formatCurrency(item.price_each)}
                          </TableCell>
                          <TableCell align="right" className="whitespace-nowrap font-medium">
                            {formatCurrency(item.price_per_100)}
                          </TableCell>
                          <TableCell align="center" className="whitespace-nowrap">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => addToCart(item)}
                              className="h-8 px-3 text-xs flex items-center gap-1 mx-auto"
                              title="Add to list"
                            >
                              <Plus className="h-4 w-4" />
                              <span>Add</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between">
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        Page {page} of {totalPages}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="h-8 px-3 text-xs"
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="h-8 px-3 text-xs"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Cart SlideOver */}
      <SlideOver
        open={cartOpen}
        onOpenChange={setCartOpen}
        title={`My List (${cart.length} items)`}
        width="w-full max-w-3xl"
      >
        <div className="space-y-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ShoppingCart className="h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Your list is empty
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Add items from the price list to create your list
              </p>
            </div>
          ) : (
            <>
              {/* Cart Actions */}
              <div className="flex gap-2 justify-end pb-4 border-b border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrint}
                  disabled={generatingPdf}
                  className="h-8 px-3 text-xs flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  Print
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleDownloadPDF}
                  disabled={generatingPdf}
                  className="h-8 px-3 text-xs flex items-center gap-2"
                >
                  {generatingPdf ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Download PDF
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearCart}
                  className="h-8 px-3 text-xs flex items-center gap-2 text-red-700 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4" />
                  Clear All
                </Button>
              </div>

              {/* Cart Items Table */}
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead align="left">Part Number</TableHead>
                    <TableHead align="left">Description</TableHead>
                    <TableHead align="left">Category</TableHead>
                    <TableHead align="center">Qty</TableHead>
                    <TableHead align="right">Price Each</TableHead>
                    <TableHead align="right">Line Total</TableHead>
                    <TableHead align="center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cart.map((item, index) => {
                    const quantity = item.quantity || 1;
                    const priceEach = parseFloat(item.price_each) || 0;
                    const lineTotal = quantity * priceEach;

                    return (
                      <TableRow key={index}>
                        <TableCell align="left" className="font-medium">
                          {item.article_no || '-'}
                        </TableCell>
                        <TableCell align="left">
                          {item.description || '-'}
                        </TableCell>
                        <TableCell align="left">
                          {item.category || '-'}
                        </TableCell>
                        <TableCell align="center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="icon-sm"
                              onClick={() => updateCartQuantity(item.article_no, quantity - 1)}
                              className="h-8 w-8 p-0 text-xs"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <Input
                              type="number"
                              min="1"
                              value={quantity}
                              onChange={(e) => updateCartQuantity(item.article_no, parseInt(e.target.value) || 1)}
                              className="h-8 w-16 px-2 text-center"
                            />
                            <Button
                              variant="outline"
                              size="icon-sm"
                              onClick={() => updateCartQuantity(item.article_no, quantity + 1)}
                              className="h-8 w-8 p-0 text-xs"
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.price_each)}
                        </TableCell>
                        <TableCell align="right" className="font-medium">
                          {formatCurrency(lineTotal)}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => removeFromCart(item.article_no)}
                            className="h-8 w-8 p-0 text-xs text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Remove item"
                            aria-label="Remove item"
                          >
                            <X className="h-5 w-5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <tfoot className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <td colSpan="5" className="px-4 py-3 text-right text-xs font-semibold text-gray-900 dark:text-gray-100">
                      Total:
                    </td>
                    <td className="px-4 py-3 text-right text-xs font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(
                        cart.reduce((sum, item) => {
                          const quantity = item.quantity || 1;
                          const price = parseFloat(item.price_each) || 0;
                          return sum + (quantity * price);
                        }, 0)
                      )}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </>
          )}
        </div>
      </SlideOver>

      <ToastContainer />
    </Layout>
  );
}
